import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  Datastore,
  DatastoreCollection,
  DatastoreDocument,
} from '@freelancer/datastore';
import {
  MembershipBenefitsCollection,
  ProfileViewUsersCollection,
  UsersFollowCollection,
  UsersRecommendCollection,
} from '@freelancer/datastore/collections';
import { AnchorScroll } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { SplitButtonOption } from '@freelancer/ui/split-button';
import { StickyFooterPosition } from '@freelancer/ui/sticky-footer';
import { FontWeight } from '@freelancer/ui/text';
import { UserFollowStatusApi } from 'api-typings/users/users';
import {
  FollowUserToastItem,
  HireMeSplitButtonOptions,
  RecommendUserToastItem,
} from 'app/hire-me/hire-me.component';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from '../user-profile-award-project.types';
import { UserProfileViewState } from '../user-profile-editable-ui/user-profile-editable-ui.model';
import { SplitButtonClickEvent } from '../user-profile-hire-me/user-profile-hire-me.component';
import { UserProfileSection } from '../user-profile.component';

@Component({
  selector: 'app-user-profile-footer',
  template: `
    <fl-sticky-footer [position]="StickyFooterPosition.FIXED">
      <fl-bit class="UserProfileFooter">
        <app-user-profile-hire-me-action-state
          *ngIf="!(isViewerOwner$ | flAsync); else selfView"
          class="HireMeActionContainer"
          [awardProjectHireMeState$]="awardProjectHireMeState$"
        >
          <ng-container loading-state>
            <ng-template [ngTemplateOutlet]="loading"></ng-template>
          </ng-container>

          <ng-container award-project-state>
            <ng-container
              *ngIf="awardProjectBid$ | flAsync as awardProjectModel"
            >
              <fl-button
                i18n="Award button label for footer"
                flTrackingLabel="AwardMeBannerAwardButton"
                [color]="ButtonColor.SUCCESS"
                [fragment]="'proposals'"
                [display]="'block'"
                [busy]="awardProjectStateBusy"
                [link]="awardProjectModel.projectLink"
                [size]="ButtonSize.SMALL"
                [queryParams]="{ awardBid: awardProjectModel.bidId }"
                (click)="awardProjectStateBusy = true"
              >
                Award
              </fl-button>
            </ng-container>
          </ng-container>

          <ng-container hire-me-state>
            <fl-split-button
              *ngIf="
                hireMeUser$ | flAsync as hireMeUser;
                else loggedOutSplitButton
              "
              i18n="User Profile logged out Hire Me footer button"
              flTrackingLabel="LoggedOutFooterHireMeButtonClick"
              [display]="'block'"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [options]="hireMeSplitButtonOptions$ | flAsync"
              (primaryClicked)="handleHireMeClicked()"
              (optionSelected)="onOptionSelected($event, hireMeUser)"
            >
              Hire {{ hireMeUser?.profileUserDisplayName }}
            </fl-split-button>
            <ng-template #loggedOutSplitButton>
              <fl-split-button
                i18n="User Profile logged out Hire Me footer button"
                flTrackingLabel="LoggedOutFooterHireMeButtonClick"
                [display]="'block'"
                [color]="ButtonColor.SECONDARY"
                [size]="ButtonSize.SMALL"
                [options]="hireMeSplitButtonOptionsLoggedOut"
                (primaryClicked)="handleHireMeClicked()"
                (optionSelected)="onOptionSelected($event)"
              >
                Hire {{ (hireMeUser$ | flAsync)?.profileUserDisplayName }}
              </fl-split-button>
            </ng-template>
          </ng-container>

          <ng-template #loading>
            <fl-button
              flTrackingLabel="LoggedOutFooterHireMeLoadingButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [disabled]="true"
              [display]="'block'"
              [busy]="true"
            >
              <!-- Text is here to ensure the button is full size. Does not need translating. -->
              {{ 'LOADING' }}
            </fl-button>
          </ng-template>
        </app-user-profile-hire-me-action-state>

        <ng-template #selfView>
          <fl-button
            *ngIf="
              (viewState$ | flAsync) === UserProfileViewState.VIEW;
              else editingView
            "
            i18n="Edit profile footer button"
            flTrackingLabel="UserProfileFooterEditProfile"
            [color]="ButtonColor.SECONDARY"
            [display]="'block'"
            [size]="ButtonSize.SMALL"
            (click)="handleClickEditProfile()"
          >
            Edit Profile
          </fl-button>

          <ng-template #editingView>
            <fl-button
              class="CancelEditingButton"
              i18n="Cancel editing profile footer button"
              flTrackingLabel="UserProfileFooterEditProfileCancel"
              [color]="ButtonColor.DEFAULT"
              [flMarginRight]="Margin.XXSMALL"
              [display]="'block'"
              [size]="ButtonSize.SMALL"
              [disabled]="savingSummaryChanges$ | flAsync"
              (click)="cancelEditProfile.emit()"
            >
              Cancel
            </fl-button>

            <fl-button
              i18n="Save editing profile footer button"
              flTrackingLabel="UserProfileFooterEditProfileSave"
              [color]="ButtonColor.SECONDARY"
              [display]="'block'"
              [busy]="savingSummaryChanges$ | flAsync"
              [size]="ButtonSize.SMALL"
              (click)="saveEditProfile.emit()"
            >
              Save Changes
            </fl-button>
          </ng-template>
        </ng-template>
      </fl-bit>
    </fl-sticky-footer>
  `,
  styleUrls: ['./user-profile-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileFooterComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ContainerSize = ContainerSize;
  FontWeight = FontWeight;
  IconSize = IconSize;
  Margin = Margin;
  StickyFooterPosition = StickyFooterPosition;
  UserProfileViewState = UserProfileViewState;

  @Input() awardProjectBid$: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$: Rx.Observable<AwardProjectHireMeState>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isViewerOwner$: Rx.Observable<boolean>;
  @Input() userDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() viewState$: Rx.Observable<UserProfileViewState>;
  @Input() savingSummaryChanges$: Rx.Observable<boolean>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;

  @Output() editProfile = new EventEmitter<void>();
  @Output() saveEditProfile = new EventEmitter<void>();
  @Output() cancelEditProfile = new EventEmitter<void>();
  @Output() optionClicked = new EventEmitter<SplitButtonClickEvent>();
  @Output() showToastAlert = new EventEmitter<
    FollowUserToastItem | RecommendUserToastItem
  >();

  awardProjectStateBusy = false;
  followedUsersCount: number;
  maxFollowLimit: number;
  userFollowsCollection: DatastoreCollection<UsersFollowCollection>;
  userRecommendCollection: DatastoreCollection<UsersRecommendCollection>;
  hireMeSplitButtonOptions$: Rx.Observable<readonly SplitButtonOption[]>;
  hireMeSplitButtonOptionsLoggedOut: readonly SplitButtonOption[];

  constructor(
    private anchorScroll: AnchorScroll,
    private datastore: Datastore,
  ) {}

  ngOnInit() {
    const hasFollowBenefit$ = this.datastore
      .document<MembershipBenefitsCollection>(
        'membershipBenefits',
        'employer_following',
      )
      .valueChanges()
      .pipe(
        tap(benefit => {
          this.maxFollowLimit = benefit.value;
        }),
        map(benefit => benefit.value !== 0),
      );

    this.userFollowsCollection = this.datastore.collection<UsersFollowCollection>(
      'usersFollow',
    );

    this.userRecommendCollection = this.datastore.collection<UsersRecommendCollection>(
      'usersRecommend',
      query =>
        query.where(
          'recommendedUserId',
          '==',
          this.hireMeUser$.pipe(map(user => user.profileUserId)),
        ),
    );

    this.hireMeSplitButtonOptionsLoggedOut = [
      { copy: `Hire` },
      { copy: HireMeSplitButtonOptions.INVITE },
    ];

    this.hireMeSplitButtonOptions$ = Rx.combineLatest([
      hasFollowBenefit$,
      this.userFollowsCollection.valueChanges(),
      this.userRecommendCollection.valueChanges(),
      this.hireMeUser$,
    ]).pipe(
      map(([hasFollowBenefit, userFollows, userRecommends, hireMeUser]) => {
        this.followedUsersCount = userFollows.length;
        const hasRecommendedProfile = userRecommends.length > 0;
        const isFollowingUser = userFollows.find(
          userFollow => userFollow.followedUserId === hireMeUser.profileUserId,
        );

        const userFollowingButton = isFollowingUser
          ? { copy: HireMeSplitButtonOptions.UNFOLLOW }
          : { copy: HireMeSplitButtonOptions.FOLLOW };
        const defaultHireMeOptions = [
          { copy: HireMeSplitButtonOptions.INVITE },
        ];
        return [
          { copy: `Hire ${hireMeUser.profileUserDisplayName}` },
          ...defaultHireMeOptions,
          ...(hasFollowBenefit ? [userFollowingButton] : []),
          ...(!hasRecommendedProfile
            ? [{ copy: HireMeSplitButtonOptions.RECOMMEND }]
            : []),
        ];
      }),
    );
  }

  handleClickEditProfile(): void {
    this.anchorScroll.scrollTo(UserProfileSection.SUMMARY);
    this.editProfile.emit();
  }

  handleHireMeClicked(): void {
    this.optionClicked.emit(SplitButtonClickEvent.HIRE_ME);
  }

  onOptionSelected(option: SplitButtonOption, hireMeUser?: HireMeUser) {
    switch (option.copy) {
      case HireMeSplitButtonOptions.INVITE:
        this.handleHireMeInviteOption();
        break;
      case HireMeSplitButtonOptions.FOLLOW:
        if (!hireMeUser) {
          return;
        }
        this.handleHireMeFollowUserOption(hireMeUser);
        break;
      case HireMeSplitButtonOptions.UNFOLLOW:
        if (!hireMeUser) {
          return;
        }
        this.handleHireMeUnfollowUserOption(hireMeUser);
        break;
      case HireMeSplitButtonOptions.RECOMMEND:
        if (!hireMeUser) {
          return;
        }
        this.handleHireMeRecommendUserOption(hireMeUser);
        break;
      default:
        break;
    }
  }

  handleHireMeInviteOption(): void {
    this.optionClicked.emit(SplitButtonClickEvent.INVITE);
  }

  handleHireMeFollowUserOption(hireMeUser: HireMeUser): void {
    if (this.followedUsersCount >= this.maxFollowLimit) {
      return;
    }

    this.userFollowsCollection
      .push({
        followedUserId: hireMeUser.profileUserId,
        followedByUserId: hireMeUser.authId,
        id: hireMeUser.profileUserId,
        status: UserFollowStatusApi.FOLLOWING,
      })
      .then(result => {
        let toastAlertType: FollowUserToastItem;
        if (result.status === 'error') {
          switch (result.errorCode) {
            case 'USER_FOLLOW_USER_DOES_NOT_ALLOW_FOLLOWING':
              toastAlertType =
                FollowUserToastItem.FOLLOW_ERROR_USER_DOES_NOT_ALLOW_FOLLOWING;
              break;
            case 'USER_FOLLOW_ALREADY_FOLLOWING':
              toastAlertType =
                FollowUserToastItem.FOLLOW_ERROR_ALREADY_FOLLOWING;
              break;
            default:
              toastAlertType = FollowUserToastItem.FOLLOW_ERROR;
          }
        } else {
          toastAlertType = FollowUserToastItem.FOLLOW_SUCCESS;
        }
        this.setToastAlert(toastAlertType);
      });
  }

  handleHireMeUnfollowUserOption(hireMeUser: HireMeUser): void {
    this.userFollowsCollection
      .remove(hireMeUser.profileUserId)
      .then(result =>
        result.status === 'error'
          ? this.setToastAlert(FollowUserToastItem.UNFOLLOW_ERROR)
          : this.setToastAlert(FollowUserToastItem.UNFOLLOW_SUCCESS),
      );
  }

  handleHireMeRecommendUserOption(hireMeUser: HireMeUser): void {
    this.userRecommendCollection
      .push({
        id: hireMeUser.profileUserId,
        recommendedByUserId: hireMeUser.authId,
        recommendedUserId: hireMeUser.profileUserId,
      })
      .then(result =>
        result.status === 'error'
          ? this.setToastAlert(RecommendUserToastItem.RECOMMEND_ERROR)
          : this.setToastAlert(RecommendUserToastItem.RECOMMEND_SUCCESS),
      );
  }

  setToastAlert(item: FollowUserToastItem | RecommendUserToastItem): void {
    this.showToastAlert.emit(item);
  }
}
